import { createTourStore, type TourState, useMe, useUpdateMe } from "@quortex/front-common"
import { useMutation, useSuspenseQuery } from "@tanstack/react-query"
import { useEffect } from "react"
import { create } from "zustand"

const usePrivateStore = create<TourState>((set, get) =>
  createTourStore({
    steps: [
      {
        body: (
          <div className="flex w-80 flex-col gap-2">
            <p>
              A <strong>segment</strong> is a category of viewers for which a balancing
              configuration applies.
            </p>
            <p>
              The root segment represents <strong>all viewers.</strong>
            </p>
            <p>
              Refine balancing for specific viewers by creating <strong>sub segments.</strong>
            </p>
          </div>
        ),
        placement: "bottom",
        title: "You just configured the root segment"
      }
    ]
  })(set, get)
)

export const useSegmentationRootTour = <T,>(selector: (state: TourState) => T) => {
  // TODO: Replace with useMe tours when flags are available
  const { data: me } = useSuspenseQuery(useMe())
  const { mutateAsync: updateMe } = useMutation(useUpdateMe())
  const seenTours = me.seenTours
  const start = usePrivateStore(state => state.start)
  const setInitialized = usePrivateStore(state => state.setInitialized)
  const initialized = usePrivateStore(state => state.initialized)
  const stopped = usePrivateStore(state => state.currentIndex === -1)
  const seen = seenTours.includes("switch_root_segment")

  useEffect(() => {
    if (!initialized && !seen) {
      start()
      setInitialized(true)
    }
  }, [initialized, seen, start, setInitialized])

  useEffect(() => {
    if (initialized && stopped !== seen) {
      if (stopped) {
        updateMe({
          seenTours: stopped
            ? Array.from(new Set([...me.seenTours, "switch_root_segment"]))
            : me.seenTours.filter(tour => tour === "switch_root_segment")
        })
      }
    }
  }, [stopped, initialized, seen, me, updateMe])

  return usePrivateStore(selector)
}
